import {GetStaticPaths, GetStaticProps} from 'next'
import React, {useRef} from 'react'
import getPageData from '../src/lib/contentful/getPageData'
import Components, {getComponentName} from '../src/components'
import getMenu from '../src/lib/contentful/getMenu'
import Header, {IHeader} from '@miro-site/components_header'
import Footer, {IFooter} from '@miro-site/components_footer'
import {LandingPage, Translation} from '../src/lib/contentful/types'

import type {Entry} from 'contentful'
import styled from 'styled-components'
import {LocalizationProvider} from '@miro-site/localization'
import {replaceStaticPaths} from '../src/lib/contentful/helpers'
import getPageBySlug from '../src/lib/contentful/getPageBySlug'
import {useRouter} from 'next/router'
import Head from 'next/head'
import {PreviewMode} from '@miro-site/core'
import Script from 'next/script'

import {theme as acqusitionHeaderTheme} from '@miro-site/components_acquisition-header'

import logger from '../src/logger/logger'

export const getStaticPaths: GetStaticPaths = async () => {
  let paths = []

  // We are using fallback behaviour for our dev mode to make performance better
  if (process.env.IS_DEVELOPMENT_SERVER) {
    return {
      paths,
      fallback: 'blocking',
    }
  }

  const pageData = await getPageData()

  paths = Object.keys(pageData).map(slug => {
    return {
      params: {slug: slug.split('/')},
    }
  })

  return {
    paths,
    fallback: true,
  }
}

export type LandingPageProps = {
  header: IHeader | null
  footer: IFooter | null
  page: Entry<LandingPage>
  translations: Translation[]
  preview?: boolean
}

export const getStaticProps: GetStaticProps<LandingPageProps> = async context => {
  const slug = Array.isArray(context.params.slug)
    ? context.params.slug.join('/')
    : context.params.slug

  const preview = !!context.preview

  const log = logger.child({slug: slug, preview})

  const revalidate = process.env.NEXT_REVALIDATE_TIMEOUT

  log.info('Starting regeneration...')

  const pageData = await getPageBySlug(slug, preview)

  const {entry: page, translations = []} = pageData || {}

  if (!page) {
    log.info('Page not found')

    return {
      notFound: true,
      revalidate: revalidate ? Number(revalidate) : false,
    }
  }

  log.info(`Found page with locale: ${page.sys.locale}`)

  let header: LandingPageProps['header'] = null
  let footer: LandingPageProps['footer'] = null

  if (!page.fields.acquisitionPage) {
    log.info('Fetching menu data...')

    const menu = await getMenu(page.sys.locale, preview)

    header = menu[0] || null
    footer = menu[1] || null
  }

  log.info('Regeneration finished')

  return {
    props: {
      header,
      footer,
      page,
      translations,
      preview,
    },
    revalidate: revalidate ? Number(revalidate) : false,
  }
}

const Container = styled.div`
  position: relative;
  min-height: 100%;
  height: auto !important;
`

const HeaderWrapper = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 200;

  &.mobile-menu-opened {
    position: fixed;
    z-index: 700;
  }
`

const Page = (props: LandingPageProps): React.ReactElement => {
  const {header, footer, page, translations, preview} = props

  const router = useRouter()

  if (router.isFallback) {
    return <></>
  }

  let headerBackground

  const layout = page.fields.layout.reduce((output, section, i) => {
    if (section.hidden) {
      return output
    }

    const Component = Components[getComponentName(section)]

    if (!Component) {
      return output
    }

    replaceStaticPaths(section.values)

    output.push(<Component key={section.id} {...section.values} />)

    // Guess the background for Acquisition Header component
    if (
      page.fields.acquisitionPage &&
      i === 0 &&
      typeof section.values.background === 'string' &&
      acqusitionHeaderTheme.background[section.values.background]
    ) {
      headerBackground = section.values.background
    }

    return output
  }, [])

  const headerWrapperRef = useRef<HTMLDivElement>(null)

  const headerComponent = header ? (
    <HeaderWrapper
      css={`
        display: ${page.sys.id === 'zgLjMzF1gMg3IVIpUKDaQ' ? 'none' : 'block'};
      `}
      ref={headerWrapperRef}
    >
      <Header containerNode={headerWrapperRef.current} {...header} />
    </HeaderWrapper>
  ) : (
    <Components.AcquisitionHeader background={headerBackground} />
  )
  const footerComponent = footer ? <Footer {...footer} /> : <Components.AcquisitionFooter />

  const defaultOpenGraphImage = `${
    process.env.NEXT_PUBLIC_ASSET_PREFIX || ''
  }/assets/images/miro.png`

  return (
    <LocalizationProvider
      detectLocale={false}
      detectTranslations={false}
      data={{
        currentLocale: page.sys.locale,
        translations: translations.map(t => ({
          locale: t.locale,
          url: t.url ? `/${t.url}/` : '/',
        })),
      }}
    >
      <Head>
        <title>{page.fields.title}</title>
        <meta name="description" content={page.fields.description} />

        <link rel="canonical" href={page.fields.canonicalUrl} />

        {translations.map((l, i) => {
          const hreflang = l.isDefault ? 'x-default' : l.locale

          return (
            <link
              key={i}
              rel="alternate"
              href={[
                process.env.NEXT_PUBLIC_PROTO || 'https://',
                process.env.NEXT_PUBLIC_HOST || 'miro.com',
                `/${l.url}/`,
              ].join('')}
              hrefLang={hreflang}
            />
          )
        })}

        <meta property="og:site_name" content="https://miro.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:determiner" content="a" />

        <meta property="og:title" content={page.fields.openGraphTitle || page.fields.title} />
        <meta
          property="og:description"
          content={page.fields.openGraphDescription || page.fields.description}
        />
        <meta
          property="og:image"
          content={
            page.fields.openGraphImage
              ? page.fields.openGraphImage.fields.file.url
              : defaultOpenGraphImage
          }
        />

        {page.fields.noIndex && <meta name="robots" content="noindex" />}

        <meta name="twitter:card" content="summary" />
        <meta name="twitter:url" content="https://miro.com/" />
        <meta name="twitter:title" content={page.fields.openGraphTitle || page.fields.title} />
        <meta
          name="twitter:description"
          content={page.fields.openGraphDescription || page.fields.description}
        />
        <meta
          name="twitter:image"
          content={
            page.fields.openGraphImage
              ? page.fields.openGraphImage.fields.file.url
              : defaultOpenGraphImage
          }
        />
      </Head>

      {headerComponent}

      <Container>
        <main>{layout}</main>
      </Container>
      {footerComponent}

      {page.fields.customScripts &&
        page.fields.customScripts.map(item => (
          <Script key={item.fields.file.url} src={item.fields.file.url} strategy="lazyOnload" />
        ))}

      {preview && (
        <PreviewMode
          stopPreviewPath={`${
            process.env.NEXT_PUBLIC_API_BASE_PATH ? process.env.NEXT_PUBLIC_API_BASE_PATH : '/api'
          }/stop-preview/`}
        />
      )}
    </LocalizationProvider>
  )
}

export default Page
